import React, { useState } from "react";
import {
	Target,
	Clock,
	Users,
	Save,
	ChevronDown,
	ChevronUp,
	Filter,
	Calendar,
	Edit3,
	XCircle,
	Trash2,
} from "lucide-react";
import CustomModal from "./CustomModal";

const GOAL_STATUSES = [
	{ value: "in_progress", label: "In Progress", color: "#d15a36" },
	{ value: "achieved", label: "Achieved", color: "#28a745" },
	{ value: "missed", label: "Missed", color: "#dc3545" },
	{ value: "abandoned", label: "Abandoned", color: "#6c757d" },
];

const GOAL_CATEGORIES = [
	"Strength",
	"Cardio",
	"Flexibility",
	"Nutrition",
	"Weight Loss",
	"Endurance",
	"Other",
];

const TrainerGoals = ({
	allGoals,
	goalDeadline,
	users,
	onSetDeadline,
	onUpdateGoal,
	onDeleteGoal,
	loading,
}) => {
	const [showDeadlineForm, setShowDeadlineForm] = useState(false);
	const [deadlineData, setDeadlineData] = useState({
		challengeName: goalDeadline?.challengeName || "",
		deadlineDate: goalDeadline?.deadlineDate || "",
		durationWeeks: "",
	});
	const [filterStatus, setFilterStatus] = useState("all");
	const [filterUser, setFilterUser] = useState("all");
	const [expandedGoals, setExpandedGoals] = useState({});
	const [editingGoal, setEditingGoal] = useState(null);
	const [editFormData, setEditFormData] = useState({
		title: "",
		description: "",
		category: "",
		status: "in_progress",
	});
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		onConfirm: null,
	});

	const handleSetDeadline = async (e) => {
		e.preventDefault();
		if (!deadlineData.deadlineDate) {
			setModal({
				show: true,
				title: "Missing Deadline",
				message: "Please set a deadline date.",
				type: "warning",
			});
			return;
		}

		try {
			await onSetDeadline({
				challengeName: deadlineData.challengeName.trim(),
				deadlineDate: deadlineData.deadlineDate,
			});
			setShowDeadlineForm(false);
			setModal({
				show: true,
				title: "Deadline Set",
				message: "Challenge deadline has been set for all users!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to set deadline.",
				type: "error",
			});
		}
	};

	// Quick set deadline from weeks
	const handleWeeksChange = (weeks) => {
		setDeadlineData((prev) => {
			const date = new Date();
			date.setDate(date.getDate() + weeks * 7);
			return {
				...prev,
				durationWeeks: weeks,
				deadlineDate: date.toISOString().split("T")[0],
			};
		});
	};

	const toggleExpand = (goalId) => {
		setExpandedGoals((prev) => ({ ...prev, [goalId]: !prev[goalId] }));
	};

	const getStatusInfo = (status) => {
		return GOAL_STATUSES.find((s) => s.value === status) || GOAL_STATUSES[0];
	};

	const getUserName = (userId) => {
		const foundUser = users.find((u) => u.uid === userId);
		return foundUser?.name || "Unknown User";
	};

	// Edit goal handlers
	const startEdit = (goal) => {
		setEditingGoal(goal);
		setEditFormData({
			title: goal.title,
			description: goal.description,
			category: goal.category || "",
			status: goal.status,
		});
	};

	const cancelEdit = () => {
		setEditingGoal(null);
		setEditFormData({
			title: "",
			description: "",
			category: "",
			status: "in_progress",
		});
	};

	const handleEditSubmit = async (e) => {
		e.preventDefault();
		if (!editFormData.title.trim() || !editFormData.description.trim()) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Title and description are required.",
				type: "warning",
			});
			return;
		}

		try {
			await onUpdateGoal(editingGoal.id, editFormData);
			cancelEdit();
			setModal({
				show: true,
				title: "Goal Updated",
				message: "Goal has been updated successfully.",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to update goal.",
				type: "error",
			});
		}
	};

	// Close/delete goal handler
	const handleCloseGoal = (goal) => {
		setConfirmModal({
			show: true,
			title: "Close Goal",
			message: `Are you sure you want to close and remove "${goal.title}" by ${goal.userName || getUserName(goal.userId)}? This cannot be undone.`,
			onConfirm: async () => {
				setConfirmModal({
					show: false,
					title: "",
					message: "",
					onConfirm: null,
				});
				try {
					await onDeleteGoal(goal.id);
					setModal({
						show: true,
						title: "Goal Closed",
						message: "Goal has been removed.",
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to close goal.",
						type: "error",
					});
				}
			},
		});
	};

	// Filter goals
	const filteredGoals = allGoals.filter((goal) => {
		if (filterStatus !== "all" && goal.status !== filterStatus) return false;
		if (filterUser !== "all" && goal.userId !== filterUser) return false;
		return true;
	});

	// Get unique users who have goals
	const usersWithGoals = [...new Set(allGoals.map((g) => g.userId))];

	// Stats
	const totalGoals = allGoals.length;
	const inProgressCount = allGoals.filter(
		(g) => g.status === "in_progress",
	).length;
	const achievedCount = allGoals.filter((g) => g.status === "achieved").length;

	// Calculate days remaining
	const getDaysRemaining = () => {
		if (!goalDeadline?.deadlineDate) return null;
		const now = new Date();
		const deadline = new Date(goalDeadline.deadlineDate);
		const diffMs = deadline - now;
		return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
	};

	const daysRemaining = getDaysRemaining();

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h5 className="mb-0">
					<Target className="me-2" size={20} />
					Member Goals
				</h5>
				<button
					className="btn btn-primary btn-sm"
					onClick={() => setShowDeadlineForm(!showDeadlineForm)}
					disabled={loading}
				>
					{showDeadlineForm ? (
						"Cancel"
					) : (
						<>
							<Clock size={16} className="me-1" />
							Set Deadline
						</>
					)}
				</button>
			</div>

			{/* Current Deadline Info */}
			{goalDeadline?.deadlineDate && (
				<div
					className="mb-3 p-3 d-flex align-items-center gap-2"
					style={{
						backgroundColor:
							daysRemaining <= 7
								? "rgba(220,53,69,0.15)"
								: "rgba(209,90,54,0.15)",
						borderRadius: "8px",
						border: `1px solid ${daysRemaining <= 7 ? "rgba(220,53,69,0.3)" : "rgba(209,90,54,0.3)"}`,
					}}
				>
					<Clock
						size={18}
						style={{ color: daysRemaining <= 7 ? "#dc3545" : "#d15a36" }}
					/>
					<div>
						<strong style={{ color: "#fff" }}>
							{goalDeadline.challengeName || "Current Challenge"}
						</strong>
						<span
							style={{ color: "rgba(255,255,255,0.7)", marginLeft: "0.5rem" }}
						>
							{daysRemaining > 0
								? `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining`
								: daysRemaining === 0
									? "Deadline is today!"
									: `Ended ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? "s" : ""} ago`}
						</span>
						<small style={{ color: "rgba(255,255,255,0.5)", display: "block" }}>
							Deadline:{" "}
							{new Date(goalDeadline.deadlineDate).toLocaleDateString("en-GB", {
								day: "numeric",
								month: "long",
								year: "numeric",
							})}
						</small>
					</div>
				</div>
			)}

			{/* Deadline Form */}
			{showDeadlineForm && (
				<div
					className="mb-4 p-3"
					style={{
						backgroundColor: "#2a2a2a",
						borderRadius: "8px",
						border: "1px solid #444",
					}}
				>
					<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
						<Calendar size={16} className="me-2" />
						Set Challenge Deadline
					</h6>
					<p
						style={{
							color: "rgba(255,255,255,0.6)",
							fontSize: "0.85rem",
							marginBottom: "1rem",
						}}
					>
						This deadline will be visible to all members for their goals.
					</p>
					<form onSubmit={handleSetDeadline}>
						<div className="row g-3">
							<div className="col-12 col-md-6">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Challenge Name (optional)
								</label>
								<input
									type="text"
									className="form-control"
									value={deadlineData.challengeName}
									onChange={(e) =>
										setDeadlineData({
											...deadlineData,
											challengeName: e.target.value,
										})
									}
									placeholder="e.g. 8-Week Summer Challenge"
									disabled={loading}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
							</div>
							<div className="col-12 col-md-3">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Quick Set (weeks)
								</label>
								<select
									className="form-select"
									value={deadlineData.durationWeeks}
									onChange={(e) => handleWeeksChange(parseInt(e.target.value))}
									disabled={loading}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								>
									<option value="">Choose...</option>
									<option value="4">4 weeks</option>
									<option value="6">6 weeks</option>
									<option value="8">8 weeks</option>
									<option value="10">10 weeks</option>
									<option value="12">12 weeks</option>
								</select>
							</div>
							<div className="col-12 col-md-3">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Deadline Date *
								</label>
								<input
									type="date"
									className="form-control"
									value={deadlineData.deadlineDate}
									onChange={(e) =>
										setDeadlineData({
											...deadlineData,
											deadlineDate: e.target.value,
											durationWeeks: "",
										})
									}
									disabled={loading}
									min={new Date().toISOString().split("T")[0]}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
							</div>
							<div className="col-12">
								<button
									type="submit"
									className="btn btn-success"
									disabled={loading}
								>
									<Save size={16} className="me-1" />
									{loading ? "Saving..." : "Set Deadline"}
								</button>
							</div>
						</div>
					</form>
				</div>
			)}

			{/* Stats Row */}
			<div className="row g-3 mb-3">
				<div className="col-4">
					<div
						className="p-2 text-center"
						style={{
							backgroundColor: "#2a2a2a",
							borderRadius: "8px",
							border: "1px solid #333",
						}}
					>
						<h4 style={{ color: "#d15a36", margin: 0 }}>{totalGoals}</h4>
						<small style={{ color: "rgba(255,255,255,0.5)" }}>
							Total Goals
						</small>
					</div>
				</div>
				<div className="col-4">
					<div
						className="p-2 text-center"
						style={{
							backgroundColor: "#2a2a2a",
							borderRadius: "8px",
							border: "1px solid #333",
						}}
					>
						<h4 style={{ color: "#d15a36", margin: 0 }}>{inProgressCount}</h4>
						<small style={{ color: "rgba(255,255,255,0.5)" }}>
							In Progress
						</small>
					</div>
				</div>
				<div className="col-4">
					<div
						className="p-2 text-center"
						style={{
							backgroundColor: "#2a2a2a",
							borderRadius: "8px",
							border: "1px solid #333",
						}}
					>
						<h4 style={{ color: "#28a745", margin: 0 }}>{achievedCount}</h4>
						<small style={{ color: "rgba(255,255,255,0.5)" }}>Achieved</small>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div
				className="mb-3 p-3 d-flex flex-wrap gap-2 align-items-center"
				style={{
					backgroundColor: "#2a2a2a",
					borderRadius: "8px",
					border: "1px solid #333",
				}}
			>
				<Filter size={16} style={{ color: "rgba(255,255,255,0.5)" }} />
				<select
					className="form-select form-select-sm"
					value={filterStatus}
					onChange={(e) => setFilterStatus(e.target.value)}
					style={{
						backgroundColor: "#1a1a1a",
						border: "1px solid #444",
						color: "#fff",
						width: "auto",
						minWidth: "140px",
					}}
				>
					<option value="all">All Statuses</option>
					{GOAL_STATUSES.map((s) => (
						<option key={s.value} value={s.value}>
							{s.label}
						</option>
					))}
				</select>
				<select
					className="form-select form-select-sm"
					value={filterUser}
					onChange={(e) => setFilterUser(e.target.value)}
					style={{
						backgroundColor: "#1a1a1a",
						border: "1px solid #444",
						color: "#fff",
						width: "auto",
						minWidth: "160px",
					}}
				>
					<option value="all">All Members</option>
					{usersWithGoals.map((uid) => (
						<option key={uid} value={uid}>
							{getUserName(uid)}
						</option>
					))}
				</select>
				<small style={{ color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>
					{filteredGoals.length} goal{filteredGoals.length !== 1 ? "s" : ""}
				</small>
			</div>

			{/* Edit Form */}
			{editingGoal && (
				<div
					className="mb-4 p-3"
					style={{
						backgroundColor: "#2a2a2a",
						borderRadius: "8px",
						border: "1px solid #d15a36",
					}}
				>
					<h6 style={{ color: "#d15a36", marginBottom: "1rem" }}>
						<Edit3 size={16} className="me-2" />
						Editing: {editingGoal.title}
						<small
							style={{ color: "rgba(255,255,255,0.5)", marginLeft: "0.5rem" }}
						>
							({editingGoal.userName || getUserName(editingGoal.userId)})
						</small>
					</h6>
					<form onSubmit={handleEditSubmit}>
						<div className="row g-3">
							<div className="col-12 col-md-6">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Title *
								</label>
								<input
									type="text"
									className="form-control"
									value={editFormData.title}
									onChange={(e) =>
										setEditFormData({ ...editFormData, title: e.target.value })
									}
									disabled={loading}
									maxLength={100}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
							</div>
							<div className="col-12 col-md-3">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Category
								</label>
								<select
									className="form-select"
									value={editFormData.category}
									onChange={(e) =>
										setEditFormData({
											...editFormData,
											category: e.target.value,
										})
									}
									disabled={loading}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								>
									<option value="">Select category</option>
									{GOAL_CATEGORIES.map((cat) => (
										<option key={cat} value={cat}>
											{cat}
										</option>
									))}
								</select>
							</div>
							<div className="col-12 col-md-3">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Status
								</label>
								<select
									className="form-select"
									value={editFormData.status}
									onChange={(e) =>
										setEditFormData({ ...editFormData, status: e.target.value })
									}
									disabled={loading}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								>
									{GOAL_STATUSES.map((s) => (
										<option key={s.value} value={s.value}>
											{s.label}
										</option>
									))}
								</select>
							</div>
							<div className="col-12">
								<label
									className="form-label small"
									style={{ color: "rgba(255,255,255,0.6)" }}
								>
									Description *
								</label>
								<textarea
									className="form-control"
									rows="3"
									value={editFormData.description}
									onChange={(e) =>
										setEditFormData({
											...editFormData,
											description: e.target.value,
										})
									}
									disabled={loading}
									maxLength={1000}
									style={{
										backgroundColor: "#1a1a1a",
										border: "1px solid #444",
										color: "#fff",
									}}
								/>
							</div>
							<div className="col-12">
								<div className="d-flex gap-2">
									<button
										type="submit"
										className="btn btn-success"
										disabled={loading}
									>
										<Save size={16} className="me-1" />
										{loading ? "Saving..." : "Save Changes"}
									</button>
									<button
										type="button"
										className="btn btn-secondary"
										onClick={cancelEdit}
										disabled={loading}
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</form>
				</div>
			)}

			{/* Goals List */}
			{filteredGoals.length === 0 ? (
				<div className="text-center py-5">
					<Target
						size={48}
						style={{ color: "rgba(255,255,255,0.2)", marginBottom: "1rem" }}
					/>
					<p style={{ color: "rgba(255,255,255,0.5)" }}>
						{allGoals.length === 0
							? "No member goals yet. Once members set goals, they'll appear here."
							: "No goals match your current filters."}
					</p>
				</div>
			) : (
				<div>
					{filteredGoals.map((goal) => {
						const isExpanded = expandedGoals[goal.id];
						const statusInfo = getStatusInfo(goal.status);

						return (
							<div
								key={goal.id}
								className="mb-3"
								style={{
									borderRadius: "8px",
									overflow: "hidden",
									border: isExpanded ? "1px solid #d15a36" : "1px solid #333",
									backgroundColor: "#1a1a1a",
									transition: "border-color 0.2s ease",
								}}
							>
								{/* Header */}
								<div
									className="p-3"
									onClick={() => toggleExpand(goal.id)}
									style={{
										cursor: "pointer",
										borderBottom: isExpanded ? "2px solid #d15a36" : "none",
									}}
								>
									<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
										<div style={{ flex: 1, minWidth: 0 }}>
											<h6
												className="mb-0"
												style={{
													color: "#fff",
													wordBreak: "break-word",
													fontSize: "1rem",
												}}
											>
												{goal.title}
											</h6>
											<small style={{ color: "rgba(255,255,255,0.5)" }}>
												<Users size={12} className="me-1" />
												{goal.userName || getUserName(goal.userId)}
											</small>
										</div>
										<div className="d-flex align-items-center gap-2 flex-shrink-0">
											{isExpanded ? (
												<ChevronUp size={18} style={{ color: "#d15a36" }} />
											) : (
												<ChevronDown
													size={18}
													style={{ color: "rgba(255,255,255,0.5)" }}
												/>
											)}
										</div>
									</div>

									{/* Meta row */}
									<div className="d-flex flex-wrap align-items-center gap-2">
										<span
											className="badge"
											style={{
												backgroundColor: statusInfo.color,
												fontSize: "0.7rem",
											}}
										>
											{statusInfo.label}
										</span>
										{goal.category && (
											<span
												className="badge"
												style={{
													backgroundColor: "#333",
													color: "#e1d0bb",
													fontSize: "0.7rem",
												}}
											>
												{goal.category}
											</span>
										)}
										<small style={{ color: "rgba(255,255,255,0.45)" }}>
											{new Date(goal.createdAt).toLocaleDateString("en-GB")}
										</small>
									</div>
								</div>

								{/* Expanded content */}
								{isExpanded && (
									<div className="p-3" style={{ overflow: "hidden" }}>
										<p
											style={{
												color: "rgba(255,255,255,0.7)",
												fontSize: "0.9rem",
												lineHeight: 1.6,
												wordBreak: "break-word",
												whiteSpace: "pre-wrap",
												marginBottom: "1rem",
											}}
										>
											{goal.description}
										</p>

										{/* Progress Notes */}
										{goal.progressNotes && goal.progressNotes.length > 0 && (
											<div className="mb-3">
												<h6
													style={{
														color: "#d15a36",
														fontSize: "0.85rem",
														marginBottom: "0.5rem",
													}}
												>
													Progress Notes
												</h6>
												{goal.progressNotes.map((note, index) => (
													<div
														key={index}
														className="mb-2 p-2"
														style={{
															backgroundColor: "#2a2a2a",
															borderRadius: "6px",
															borderLeft: "3px solid #d15a36",
														}}
													>
														<small style={{ color: "rgba(255,255,255,0.5)" }}>
															{new Date(note.date).toLocaleDateString("en-GB")}
														</small>
														<p
															className="mb-0"
															style={{
																color: "rgba(255,255,255,0.7)",
																fontSize: "0.85rem",
															}}
														>
															{note.text}
														</p>
													</div>
												))}
											</div>
										)}

										{/* Trainer Actions */}
										<div
											className="d-flex gap-2 pt-2"
											style={{ borderTop: "1px solid #333" }}
										>
											<button
												className="btn btn-sm"
												onClick={(e) => {
													e.stopPropagation();
													startEdit(goal);
												}}
												disabled={loading}
												style={{
													backgroundColor: "rgba(209,90,54,0.15)",
													color: "#d15a36",
													border: "none",
												}}
											>
												<Edit3 size={14} className="me-1" />
												Edit
											</button>
											<button
												className="btn btn-sm"
												onClick={(e) => {
													e.stopPropagation();
													handleCloseGoal(goal);
												}}
												disabled={loading}
												style={{
													backgroundColor: "rgba(220,53,69,0.15)",
													color: "#dc3545",
													border: "none",
												}}
											>
												<Trash2 size={14} className="me-1" />
												Close
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			{/* Confirm Modal */}
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type="delete"
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				onConfirm={confirmModal.onConfirm}
				confirmText="Close Goal"
			/>
		</div>
	);
};

export default TrainerGoals;
